import { isObject, useLocalStorage, useSessionStorage } from '@vueuse/core'
import { jwtDecode } from 'jwt-decode'
import { defineStore } from 'pinia'
import { computed } from 'vue'

export type TokenList = { token: string }[]

export interface AuthSession {
  state: string
  codeVerifier: string
  redirectUrl: string | null
  userId?: string
  sessionTimeout?: number
  endSessionOnClose?: string
}

export enum TokenType {
  PreAuthenticationToken = 'PreAuthenticationToken',
  UserToken = 'UserToken',
  CustomerUserToken = 'CustomerUserToken',
  ImageToken = 'ImageToken',
  PublicUserToken = 'PublicUserToken',
}

export type PreAuthCustomAccess = 'self_registration' | 'partner_self_registration' | 'sign_agreements' | 'onboarding'

export const preAuthRouteMap: Record<PreAuthCustomAccess, string> = {
  self_registration: 'SelfRegistration',
  partner_self_registration: 'VismaNetRegistration',
  sign_agreements: 'VismaNetRegistration',
  onboarding: 'CustomerOnboarding',
}

export interface AdaJwtPreAuthTokenClaims {
  token_type: TokenType.PreAuthenticationToken
  custom_access?: PreAuthCustomAccess
}
export function isPreAuthToken(payload: unknown): payload is AdaJwtPreAuthTokenClaims {
  return isObject(payload) && 'token_type' in payload && payload.token_type === TokenType.PreAuthenticationToken
}

export function isPreAuthTokenWithCustomAccess(
  payload: unknown
): payload is AdaJwtPreAuthTokenClaims & { custom_access: PreAuthCustomAccess } {
  return (
    isPreAuthToken(payload) && typeof payload.custom_access === 'string' && payload.custom_access in preAuthRouteMap
  )
}

/** Address information for a company in self-registration token */
export interface AdaJwtPreAuthTokenSelfRegistrationCompanyAddress {
  /** Address line 1 */
  adrl_1: string
  /** Address line 2 */
  adrl_2?: string
  /** Address line 3 */
  adrl_3?: string
  /** Zip/postal code */
  zip: string
  /** City name */
  city: string
  /** Country code */
  cntry: string
}

/** Company information in self-registration token */
export interface AdaJwtPreAuthTokenSelfRegistrationCompanyClaim {
  /** Organization number */
  no: string
  /** Company name */
  nm: string
  /** Whether the company is already registered */
  is_reg: boolean
  /** Address list */
  adr_list: AdaJwtPreAuthTokenSelfRegistrationCompanyAddress[]
}

/** Self-registration template information in token */
export interface AdaJwtPreAuthTokenSelfRegistrationTemplateClaim {
  /** Template ID */
  _id: string
  /** Title locale key (i18n key for the template title) */
  t_loc: string
  /** Description locale key (i18n key for the template description) */
  d_loc?: string
  /** Document ID (reference to the agreement document) */
  doc_id: string
}

/** JWT claims for self-registration pre-authentication tokens */
export interface AdaJwtPreAuthTokenSelfRegistrationClaims extends AdaJwtPreAuthTokenClaims {
  custom_access: 'self_registration'
  /** Person name (the signatory's name from BankID) */
  nm: string
  /** Company list (companies the signatory can register) */
  co_list: AdaJwtPreAuthTokenSelfRegistrationCompanyClaim[]
  /** Self-registration template list (available agreement templates) */
  srt_list: AdaJwtPreAuthTokenSelfRegistrationTemplateClaim[]
}

export interface AdaJwtPreAuthTokenVismaNetRegistrationClaims extends AdaJwtPreAuthTokenClaims {
  custom_access: 'partner_self_registration'
}

export interface AdaJwtPreAuthTokenSignAgreementsClaims extends AdaJwtPreAuthTokenClaims {
  custom_access: 'sign_agreements'
}

export function isPreAuthSelfRegistrationToken(payload: unknown): payload is AdaJwtPreAuthTokenSelfRegistrationClaims {
  return isPreAuthToken(payload) && 'custom_access' in payload && payload.custom_access === 'self_registration'
}

export function isPreAuthVismaNetRegistrationToken(
  payload: unknown
): payload is AdaJwtPreAuthTokenVismaNetRegistrationClaims {
  return isPreAuthToken(payload) && 'custom_access' in payload && payload.custom_access === 'partner_self_registration'
}

export function isPreAuthSignAgreementsToken(payload: unknown): payload is AdaJwtPreAuthTokenSignAgreementsClaims {
  return isPreAuthToken(payload) && 'custom_access' in payload && payload.custom_access === 'sign_agreements'
}

export interface AdaJwtPreAuthTokenOnboardingClaims extends AdaJwtPreAuthTokenClaims {
  custom_access: 'onboarding'
  /** The onboarding document id this token is scoped to */
  onb_id: string
  /** The invited user's id */
  u_id: string
  /** The invited user's name */
  u_name?: string
}

export function isPreAuthOnboardingToken(payload: unknown): payload is AdaJwtPreAuthTokenOnboardingClaims {
  return isPreAuthToken(payload) && 'custom_access' in payload && payload.custom_access === 'onboarding'
}

export interface AdaJwtUserTokenClaims {
  token_type: TokenType.UserToken

  token_id: string
  refresh_count: number
  ip: string
  features: 'TBD'
  rlc: 'TBD'
  rls: 'TBD'
  origin_app: string
  exp: number

  // User info
  u_id: string
  u_api_code: string | null
  u_name: string
  u_email: string
  u_favorite: string | null
  u_org?: string
  system_user?: true
  solicitor?: true

  // Partner
  p_id?: string
  vnet_tenant_c?: number

  // Account
  a_id?: string
  /**
   * Creditor onboarding state for the account this token is scoped to.
   * - `true`: every creditor the user can access in the account is onboarded.
   * - `false`: at least one creditor still needs onboarding.
   * - `undefined`: the user has no creditors in the account's scope (treat as onboarded).
   */
  c_onb?: boolean

  log_invoice_registrations?: true
  log_case_registrations?: true
  log_creditor_payments?: true
  log_creditor_cancellations?: true
  log_creditor_creditings?: true
  allow_multiple_ip_origins?: true

  signed_in_by_u_id?: string
  readonly?: true

  [prop: string]: unknown
}
export function isUserToken(payload: unknown): payload is AdaJwtUserTokenClaims {
  return isObject(payload) && 'token_type' in payload && payload.token_type === TokenType.UserToken
}

export interface AdaJwtCustomerUserTokenClaims {
  token_type: TokenType.CustomerUserToken
}
export function isCustomerUserToken(payload: unknown): payload is AdaJwtCustomerUserTokenClaims {
  return isObject(payload) && 'token_type' in payload && payload.token_type === TokenType.CustomerUserToken
}

export interface AdaJwtImageTokenClaims {
  token_type: TokenType.ImageToken

  origin_app: string
  exp: number
  [prop: string]: unknown
}
export function isImageToken(payload: unknown): payload is AdaJwtImageTokenClaims {
  return isObject(payload) && 'token_type' in payload && payload.token_type === TokenType.ImageToken
}

export interface AdaJwtPublicUserTokenClaims {
  token_type: TokenType.PublicUserToken

  origin_app: string
  exp: number
}
export function isPublicUserToken(payload: unknown): payload is AdaJwtPublicUserTokenClaims {
  return isObject(payload) && 'token_type' in payload && payload.token_type === TokenType.PublicUserToken
}

export type AdaJwtClaims =
  | AdaJwtPreAuthTokenClaims
  | AdaJwtUserTokenClaims
  | AdaJwtCustomerUserTokenClaims
  | AdaJwtImageTokenClaims
  | AdaJwtPublicUserTokenClaims

// useLocalStorage/useSessionStorage for a key should only be called once per tab since
// listenForChanges which works across tabs doesn't work in the same tab as the
// change was made.
const persistentTokens = useLocalStorage('tokens', {
  tokens: [] as { token: string }[],
  selectedToken: 0,
})

const sessionTokens = useSessionStorage('tokens', {
  tokens: [] as { token: string }[],
  selectedToken: 0,
  sessionEnd: undefined as number | undefined,
})

const authSessions = useSessionStorage<AuthSession[]>('auth_sessions', [])

/** Returns the type of user the token is for, or null if the token is not a user token */
function getTokenUserType(token: AdaJwtClaims): 'system' | 'solicitor_system_user' | 'solicitor' | 'creditor' | null {
  if (!isUserToken(token)) return null
  else if (token.u_api_code != null && token.system_user === true) return 'system'
  else if (token.solicitor === true && token.system_user === true) return 'solicitor_system_user'
  else if (token.solicitor === true) return 'solicitor'
  else return 'creditor'
}

export interface SetTokenListOptions {
  /** Uses the current storage if undefined or persistent if no tokens are currently stored */
  storage?: 'session' | 'persistent'
  sessionTimeoutMinutes?: number
}

export function getTokenType(payload: AdaJwtClaims | null | undefined): TokenType | null {
  if (payload == null) return null
  if (isPreAuthToken(payload)) return TokenType.PreAuthenticationToken
  if (isUserToken(payload)) return TokenType.UserToken
  if (isCustomerUserToken(payload)) return TokenType.CustomerUserToken
  if (isImageToken(payload)) return TokenType.ImageToken
  if (isPublicUserToken(payload)) return TokenType.PublicUserToken
  return null
}

const useAuthStore = defineStore('auth', () => {
  const authPersistence = computed<'session' | 'persistent'>(() =>
    sessionTokens.value.tokens.length > 0 ? 'session' : 'persistent'
  )

  const activeTokenStorage = computed(() =>
    authPersistence.value === 'session' ? sessionTokens.value : persistentTokens.value
  )

  const sessionEnd = computed(() => sessionTokens.value.sessionEnd)

  const isSessionExpired = computed(() => {
    if (sessionTokens.value.sessionEnd == null) return false
    return Date.now() > sessionTokens.value.sessionEnd
  })

  const tokensList = computed(() => {
    if (isSessionExpired.value) {
      return []
    }

    return activeTokenStorage.value.tokens.map(({ token }, idx) => {
      let payload = null
      try {
        payload = jwtDecode<AdaJwtClaims>(token)
        // eslint-disable-next-line no-empty
      } catch {}
      return {
        token,
        payload,
        selected: idx === activeTokenStorage.value.selectedToken,
        userType: payload != null ? getTokenUserType(payload) : null,
      }
    })
  })
  const selectedToken = computed(() => tokensList.value.find((t) => t.selected))

  function addAuthSession(
    codeVerifier: string,
    redirectUrl?: string,
    params?: Omit<AuthSession, 'state' | 'codeVerifier' | 'redirectUrl'>
  ) {
    const state = crypto.randomUUID().replace('-', '')

    authSessions.value.push({
      state,
      codeVerifier,
      redirectUrl: redirectUrl ?? null,
      userId: params?.userId,
      sessionTimeout: params?.sessionTimeout,
      endSessionOnClose: params?.endSessionOnClose,
    })
    return state
  }

  /** The pending auth session for `state`, left in place — the exchange removes it only once it has succeeded. */
  function findAuthSession(state: string): AuthSession | null {
    return authSessions.value.find((s) => s.state === state) ?? null
  }

  function popAuthSession(state: string) {
    const idx = authSessions.value.findIndex((s) => s.state === state)
    if (idx === -1) throw new Error('Auth session not found')
    const session = authSessions.value[idx]
    authSessions.value.splice(idx, 1)
    return session
  }

  function setTokenList(tokenList: TokenList, options?: SetTokenListOptions) {
    const _currentToken = selectedToken.value?.token
    const targetStorage = options?.storage ?? authPersistence.value

    if (targetStorage === 'session') {
      persistentTokens.value.tokens = []
      persistentTokens.value.selectedToken = 0

      sessionTokens.value.tokens = tokenList
      if (options?.sessionTimeoutMinutes != null) {
        sessionTokens.value.sessionEnd = Date.now() + options.sessionTimeoutMinutes * 60 * 1000
      }
    } else {
      sessionTokens.value.tokens = []
      sessionTokens.value.selectedToken = 0
      sessionTokens.value.sessionEnd = undefined

      persistentTokens.value.tokens = tokenList
    }

    try {
      selectToken(_currentToken)
    } catch {
      selectToken()
    }
  }

  function addToken(token: TokenList[number]) {
    if (authPersistence.value === 'session') {
      sessionTokens.value.tokens.push(token)
    } else {
      persistentTokens.value.tokens.push(token)
    }
  }

  function removeToken(token: { token: string }) {
    const storage = authPersistence.value === 'session' ? sessionTokens.value : persistentTokens.value
    const tokenIdx = storage.tokens.findIndex((t) => t.token === token.token)
    if (tokenIdx > -1) {
      if (storage.selectedToken >= tokenIdx && tokenIdx > 0) {
        storage.selectedToken -= 1
      }
      storage.tokens.splice(tokenIdx, 1)
    }
  }

  function updateCurrentToken(token: TokenList[number]) {
    // Last line of defence: a refresh response body without a token string must never enter the
    // bucket — it would be synced to every tab and sent as `x-api-key: undefined` (ADA-4556, M2).
    if (typeof token.token !== 'string' || token.token === '') {
      throw new TypeError('updateCurrentToken: refusing to store an entry without a token string')
    }
    const storage = authPersistence.value === 'session' ? sessionTokens.value : persistentTokens.value
    if (storage.selectedToken > -1 && selectedToken.value != null) {
      storage.tokens.splice(storage.selectedToken, 1, { token: token.token })
    }
  }

  function selectToken(token?: string) {
    const storage = authPersistence.value === 'session' ? sessionTokens.value : persistentTokens.value
    // TODO: if previously selected token was on behalf of
    // someone (payload.signed_in_by_u_id != null, remove that token
    // from the token_list
    if (token == null) {
      storage.selectedToken = 0
    } else {
      const tokenIdx = storage.tokens.findIndex((t) => t.token === token)
      if (tokenIdx === -1) throw new Error('Token not found among available tokens')
      storage.selectedToken = tokenIdx
    }
  }

  function clearAllTokens() {
    persistentTokens.value.tokens = []
    persistentTokens.value.selectedToken = 0
    sessionTokens.value.tokens = []
    sessionTokens.value.selectedToken = 0
    sessionTokens.value.sessionEnd = undefined
  }

  return {
    selectedToken,
    tokensList,
    authPersistence,
    sessionEnd,
    isSessionExpired,
    addAuthSession,
    findAuthSession,
    popAuthSession,
    setTokenList,
    updateCurrentToken,
    selectToken,
    addToken,
    removeToken,
    clearAllTokens,
  }
})

export default useAuthStore
